import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  QueueStatsPollEvent,
  QueueStatsResponse,
  QueueStatsService,
} from '../../services/queue-stats.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-queue-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './queue-stats.component.html',
  styleUrl: './queue-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueueStatsComponent implements OnInit, OnDestroy {
  @Input() title = 'Queue Stats';
  @Input() showDetails = false;
  @Input() compact = false;

  readonly visiblePollIntervalMs = environment.QUEUE_STATS_VISIBLE_POLL_INTERVAL_MS;

  isLoading = true;
  isPaused = false;
  errorMessage: string | null = null;
  stats: QueueStatsResponse['data'] | null = null;

  private readonly queueStatsService = inject(QueueStatsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private pollSub: Subscription | null = null;

  ngOnInit(): void {
    if (!this.isBrowser) {
      this.isLoading = false;
      this.errorMessage = 'Live queue monitoring starts in the browser.';
      this.mark();
      return;
    }

    this.pollSub = this.queueStatsService
      .pollQueueStats(this.visiblePollIntervalMs)
      .subscribe((event) => this.handlePollEvent(event));
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  trackWorker(_: number, worker: QueueStatsResponse['data']['workers']['details'][number]): string {
    return worker.worker_id;
  }

  formatCheckedAt(epochMs: number | null | undefined): string {
    if (!epochMs) return '—';
    const date = new Date(epochMs);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }

  formatSeconds(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'n/a';
    if (!Number.isFinite(value)) return 'n/a';
    return `${Math.round(value)}s`;
  }

  private handlePollEvent(event: QueueStatsPollEvent): void {
    if (event.type === 'paused') {
      this.isPaused = true;
      this.isLoading = !this.stats;
      this.mark();
      return;
    }

    if (event.type === 'error') {
      this.isPaused = false;
      this.isLoading = !this.stats;
      this.errorMessage = this.describePollError(event.error);
      this.mark();
      return;
    }

    const response = event.response;
    if (!response?.success) {
      this.isPaused = false;
      this.isLoading = !this.stats;
      this.errorMessage = response?.error || 'Queue stats request failed.';
      this.mark();
      return;
    }

    this.stats = response.data;
    this.isPaused = false;
    this.isLoading = false;
    this.errorMessage = null;
    this.mark();
  }

  private describePollError(error: unknown): string {
    const maybeError = error as { status?: number; message?: string; error?: { error?: string; message?: string } };
    if (maybeError?.status === 401 || maybeError?.status === 403) {
      return 'Not authorized to view queue stats. Please sign in.';
    }

    if (maybeError?.status === 503) {
      return 'Queue stats temporarily unavailable. Retrying with backoff...';
    }

    return (
      maybeError?.error?.error ||
      maybeError?.error?.message ||
      maybeError?.message ||
      'Failed to refresh queue stats. Retrying with backoff...'
    );
  }

  private mark(): void {
    this.cdr.markForCheck();
  }
}
